import httpClientProjects from '../api/httpClientProjects';

// NOTE: Backend controller route is assumed as: /api/cms/ProjectsManagement/[Action]
// following the same pattern as other CMS services (NewsManagement, NewsletterManagement, etc.)

export const getAllProjects = async ({ pageNumber = 1, pageSize = 10 } = {}) => {
  const response = await httpClientProjects.get('/api/cms/ProjectsManagement/GetAll', {
    params: { pageNumber, pageSize },
  });
  return response.data;
};

export const getProjectById = async (id) => {
  const response = await httpClientProjects.get(`/api/cms/ProjectsManagement/GetById/${id}`);
  return response.data;
};

// Create with optional cover image (multipart/form-data)
export const createProject = async (payload, coverImage) => {
  const formData = new FormData();

  if (payload?.name != null) formData.append('Name', payload.name);
  if (payload?.description != null) formData.append('Description', payload.description);

  // GroupMembers: array of { id, fullName, profilePictureUrl?, role?, isLeader }
  if (Array.isArray(payload?.groupMembers)) {
    payload.groupMembers.forEach((m, index) => {
      if (!m) return;
      const prefix = `GroupMembers[${index}]`;
      if (m.id) formData.append(`${prefix}.Id`, m.id);
      if (m.fullName) formData.append(`${prefix}.FullName`, m.fullName);
      if (m.profilePictureUrl) formData.append(`${prefix}.ProfilePictureUrl`, m.profilePictureUrl);
      if (m.role) formData.append(`${prefix}.Role`, m.role);
      if (typeof m.isLeader === 'boolean') {
        formData.append(`${prefix}.IsLeader`, String(m.isLeader));
      }
      // MemberType: 0 = Member, 1 = SubMember (ProjectMemberType enum)
      if (typeof m.memberType === 'number') {
        formData.append(`${prefix}.MemberType`, String(m.memberType));
      }
      if (m.parentMemberId) {
        formData.append(`${prefix}.ParentMemberId`, m.parentMemberId);
      }
    });
  }

  if (payload?.logoCariCode?.trim()) {
    formData.append('LogoCariCode', payload.logoCariCode.trim());
  }

  if (payload?.startDate) formData.append('StartDate', payload.startDate);
  if (payload?.endDate) formData.append('EndDate', payload.endDate);
  if (payload?.theme) formData.append('Theme', payload.theme);
  if (payload?.purpose) formData.append('Purpose', payload.purpose);

  if (typeof payload?.status === 'number') {
    formData.append('Status', String(payload.status));
  }
  if (typeof payload?.isActive === 'boolean') {
    formData.append('IsActive', String(payload.isActive));
  }

  if (coverImage instanceof File) {
    formData.append('CoverImage', coverImage, coverImage.name);
  }

  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/Create', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const updateProject = async (payload) => {
  // payload is expected to match UpdateProjectDto
  const response = await httpClientProjects.put('/api/cms/ProjectsManagement/Update', payload);
  return response.data;
};

export const deleteProject = async (id) => {
  const response = await httpClientProjects.delete(`/api/cms/ProjectsManagement/Delete/${id}`);
  return response.data;
};

export const reorderProjects = async ({ projectIdsInOrder }) => {
  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/Reorder', {
    projectIdsInOrder,
  });
  return response.data;
};

export const uploadProjectCoverImage = async ({ projectId, file }) => {
  const formData = new FormData();
  if (projectId) formData.append('ProjectId', projectId);
  if (file instanceof File) formData.append('CoverImage', file, file.name);

  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/UploadCoverImage', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const uploadProjectImage = async ({ projectId, file }) => {
  const formData = new FormData();
  if (projectId) formData.append('ProjectId', projectId);
  if (file instanceof File) formData.append('Image', file, file.name);

  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/UploadImage', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const reorderProjectImages = async ({ projectId, imageIdsInOrder }) => {
  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/ReorderImages', {
    projectId,
    imageIdsInOrder,
  });
  return response.data;
};

export const removeProjectImage = async ({ projectId, imageId }) => {
  const response = await httpClientProjects.delete(
    `/api/cms/ProjectsManagement/RemoveImage/${projectId}/${imageId}`
  );
  return response.data;
};

export const addProjectMember = async (payload) => {
  // payload: { projectId, memberId, memberType, fullName, profilePictureUrl?, role?, isLeader, parentMemberId? }
  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/AddMember', payload);
  return response.data;
};

export const removeProjectMember = async (payload) => {
  // payload: { projectId, memberId, memberType }
  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/RemoveMember', payload);
  return response.data;
};

export const setProjectLeader = async (payload) => {
  // payload: { projectId, memberId, memberType }
  const response = await httpClientProjects.post('/api/cms/ProjectsManagement/SetLeader', payload);
  return response.data;
};

export default {
  getAllProjects,
  getProjectById,
  createProject,
  updateProject,
  deleteProject,
  reorderProjects,
  uploadProjectCoverImage,
  uploadProjectImage,
  reorderProjectImages,
  removeProjectImage,
  addProjectMember,
  removeProjectMember,
  setProjectLeader,
};

